import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Plus, Edit, Trash2, MapPin, DollarSign, Star, Clock, Database } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = window.location.origin || '';

const AdminRoutesPage = () => {
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    from_location: '',
    to_location: '',
    from_coords: '',
    to_coords: '',
    distance_km: '',
    duration_min: '',
    base_price: '',
    currency: 'EUR',
    is_featured: true,
    is_active: true,
    display_order: 1
  });
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('vipgez_admin_token');
    if (!token) {
      navigate('/login-goturkeytransfer');
      return;
    }
    fetchRoutes();
  }, [navigate]);

  const fetchRoutes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('vipgez_admin_token');
      const response = await fetch(`${API_BASE}/api/admin/routes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      if (data.success) {
        setRoutes(data.routes || []);
      }
    } catch (error) {
      console.error('Failed to fetch routes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('vipgez_admin_token');
      const url = editingRoute 
        ? `${API_BASE}/api/admin/routes/${editingRoute.sqlite_id}`
        : `${API_BASE}/api/admin/routes`;
      
      const method = editingRoute ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          distance_km: parseFloat(formData.distance_km) || 0,
          duration_min: parseInt(formData.duration_min) || 0,
          base_price: parseFloat(formData.base_price) || 0,
          display_order: parseInt(formData.display_order) || 1
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success(editingRoute ? 'Route updated' : 'Route created');
        setShowForm(false);
        setEditingRoute(null);
        resetForm();
        fetchRoutes();
      } else {
        toast.error(data.error || 'Operation failed');
      }
    } catch (error) {
      toast.error('Failed to save route');
    }
  };

  const handleEdit = (route) => {
    setEditingRoute(route);
    setFormData({
      name: route.name || '',
      from_location: route.from_location || '',
      to_location: route.to_location || '',
      from_coords: route.from_coords || '',
      to_coords: route.to_coords || '',
      distance_km: route.distance_km?.toString() || '',
      duration_min: route.duration_min?.toString() || '',
      base_price: route.base_price?.toString() || '',
      currency: route.currency || 'EUR',
      is_featured: route.is_featured ?? true,
      is_active: route.is_active ?? true,
      display_order: route.display_order || 1
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!id) {
      toast.error('Cannot delete: Route ID not found');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this route?')) return;

    try {
      const token = localStorage.getItem('vipgez_admin_token');
      const response = await fetch(`${API_BASE}/api/admin/routes/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success && data.deleted_count > 0) {
        toast.success('Route deleted successfully');
        fetchRoutes();
      } else if (data.success && data.deleted_count === 0) {
        toast.error('Route not found in database');
      } else {
        toast.error(data.message || 'Failed to delete');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Delete failed');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      from_location: '',
      to_location: '',
      from_coords: '',
      to_coords: '',
      distance_km: '',
      duration_min: '',
      base_price: '',
      currency: 'EUR',
      is_featured: true,
      is_active: true,
      display_order: 1
    });
  };

  const handleSeedRoutes = async () => {
    setSeeding(true);
    try {
      const token = localStorage.getItem('vipgez_admin_token');
      const response = await fetch(`${API_BASE}/api/admin/routes/seed`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      
      if (data.success) {
        if (data.seeded) {
          toast.success(`${data.count} rota başarıyla eklendi`);
          fetchRoutes();
        } else {
          toast.info(data.message || 'Rotalar zaten mevcut');
        }
      } else {
        toast.error(data.detail || 'Rotalar yüklenemedi');
      }
    } catch (error) {
      console.error('Seed routes error:', error);
      toast.error('Başlangıç verileri yüklenemedi');
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background admin-theme" data-admin-panel="true">
      <AdminSidebar />
      
      <main className="flex-1 p-6 lg:p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-foreground" data-testid="routes-title">Popular Routes</h1>
              <p className="text-muted-foreground">Manage featured routes on your website</p>
            </div>
            <div className="flex gap-2">
              {routes.length === 0 && (
                <Button 
                  onClick={handleSeedRoutes} 
                  disabled={seeding}
                  variant="outline"
                  data-testid="seed-routes-btn"
                >
                  <Database className="h-4 w-4 mr-2" />
                  {seeding ? 'Yükleniyor...' : 'Başlangıç Verilerini Yükle'}
                </Button>
              )}
              <Button onClick={() => { setShowForm(true); setEditingRoute(null); resetForm(); }} data-testid="add-route-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Route
              </Button>
            </div>
          </div>

          {/* Form */}
          {showForm && (
            <Card className="mb-6 bg-secondary/50 border-white/10">
              <CardHeader className="border-b border-white/10">
                <CardTitle className="text-white text-lg">{editingRoute ? 'Edit Route' : 'New Route'}</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-300 block mb-1.5">Route Name</label>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="e.g., Istanbul Airport to Taksim"
                        required
                        className="bg-black/30 border-white/20 text-white placeholder:text-gray-500"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-300 block mb-1.5">Display Order</label>
                      <Input
                        name="display_order"
                        type="number"
                        value={formData.display_order}
                        onChange={handleInputChange}
                        className="bg-black/30 border-white/20 text-white"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-300 block mb-1.5">From Location</label>
                      <Input
                        name="from_location"
                        value={formData.from_location}
                        onChange={handleInputChange}
                        placeholder="Pickup address"
                        required
                        className="bg-black/30 border-white/20 text-white placeholder:text-gray-500"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-300 block mb-1.5">To Location</label>
                      <Input
                        name="to_location"
                        value={formData.to_location}
                        onChange={handleInputChange}
                        placeholder="Dropoff address"
                        required
                        className="bg-black/30 border-white/20 text-white placeholder:text-gray-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-300 block mb-1.5">From Coords (lat,lng)</label>
                      <Input
                        name="from_coords"
                        value={formData.from_coords}
                        onChange={handleInputChange}
                        placeholder="41.2761,28.7287"
                        className="bg-black/30 border-white/20 text-white placeholder:text-gray-500"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-300 block mb-1.5">To Coords (lat,lng)</label>
                      <Input
                        name="to_coords"
                        value={formData.to_coords}
                        onChange={handleInputChange}
                        placeholder="41.0370,28.9851"
                        className="bg-black/30 border-white/20 text-white placeholder:text-gray-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-300 block mb-1.5">Distance (km)</label>
                      <Input
                        name="distance_km"
                        type="number"
                        step="0.1"
                        value={formData.distance_km}
                        onChange={handleInputChange}
                        placeholder="45"
                        className="bg-black/30 border-white/20 text-white placeholder:text-gray-500"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-300 block mb-1.5">Duration (min)</label>
                      <Input
                        name="duration_min"
                        type="number"
                        value={formData.duration_min}
                        onChange={handleInputChange}
                        placeholder="50"
                        className="bg-black/30 border-white/20 text-white placeholder:text-gray-500"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-300 block mb-1.5">Base Price (EUR)</label>
                      <Input
                        name="base_price"
                        type="number"
                        step="0.01"
                        value={formData.base_price}
                        onChange={handleInputChange}
                        placeholder="45.00"
                        className="bg-black/30 border-white/20 text-white placeholder:text-gray-500"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-6 pt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="is_featured"
                        checked={formData.is_featured}
                        onChange={handleInputChange}
                        className="rounded border-white/20 bg-black/30 text-orange-500 focus:ring-orange-500"
                      />
                      <span className="text-sm text-gray-300">Featured</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleInputChange}
                        className="rounded border-white/20 bg-black/30 text-orange-500 focus:ring-orange-500"
                      />
                      <span className="text-sm text-gray-300">Active</span>
                    </label>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button type="submit" className="bg-orange-500 hover:bg-orange-600 text-white">{editingRoute ? 'Update' : 'Create'}</Button>
                    <Button type="button" variant="outline" className="border-white/20 text-gray-300 hover:bg-white/10" onClick={() => { setShowForm(false); setEditingRoute(null); }}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Routes List */}
          <Card className="bg-secondary border-border bg-secondary border-border">
            <CardHeader>
              <CardTitle className="text-foreground">Routes ({routes.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full"></div>
                </div>
              ) : routes.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  No routes created yet
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {routes.map((route, idx) => (
                    <div
                      key={route.sqlite_id || idx}
                      className="p-4 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          {route.is_featured && (
                            <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                          )}
                          <span className={`text-xs px-2 py-0.5 rounded ${route.is_active ? 'bg-green-500/10 text-green-500' : 'bg-gray-500/10 text-gray-500'}`}>
                            {route.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(route)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(route._id || route.sqlite_id || route.id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                      
                      <h3 className="font-medium text-foreground mb-2">{route.name}</h3>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex items-start gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                          <span className="text-muted-foreground truncate">{route.from_location || 'Not set'}</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <MapPin className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                          <span className="text-foreground truncate">{route.to_location || 'Not set'}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between mt-4 pt-3 border-t border-border/50">
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          <span>{route.distance_km || 0} km</span>
                          <span>{route.duration_min || 0} min</span>
                        </div>
                        <span className="font-bold text-primary">€{route.base_price || 0}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminRoutesPage;
